/**
 * Payment-schedule helpers.
 *
 * Kept free of the auth imports in `payment-api.ts` so they can be
 * used (and unit-tested) without booting the API client's module graph.
 */

import { toSubunits } from './currency';
import type { PaymentPlanInstallment } from './payment-api';

export type PlanFrequency = 'weekly' | 'biweekly' | 'monthly';

/** Offset a date by `n` payment intervals of the given frequency. Monthly offsets clamp to the
 * target month's last day, so a plan starting on the 31st lands on the 28th/30th in shorter
 * months instead of rolling into the next one (e.g. 31 Jan +1mo → 28 Feb, not 3 Mar). */
export function addFrequencyOffset(baseDate: Date, frequency: PlanFrequency, offset: number): Date {
  const result = new Date(baseDate);
  if (frequency === 'weekly') {
    result.setDate(result.getDate() + offset * 7);
  } else if (frequency === 'biweekly') {
    result.setDate(result.getDate() + offset * 14);
  } else {
    const day = result.getDate();
    result.setDate(1);
    result.setMonth(result.getMonth() + offset);
    const lastDayOfMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
    result.setDate(Math.min(day, lastDayOfMonth));
  }
  return result;
}

export interface PlanInstalment {
  /** When this instalment is taken/due. */
  date: Date;
  /** Amount of this instalment, in currency subunits. */
  subunits: number;
  /** Remaining balance after this instalment lands — 0 on the final instalment. */
  balanceAfterSubunits: number;
}

/**
 * The per-instalment amount a plan actually charges, in subunits. Every instalment
 * but the last is this figure, so it — not `totalAmount / count` — is what a plan
 * may be quoted at. The plain average rounds to a figure that appears nowhere in
 * the schedule (€496.00 over 6 averages €82.67; the plan charges €82.66).
 */
export function baseInstalmentSubunits(totalAmount: number, count: number): number {
  return Math.floor(toSubunits(totalAmount) / count);
}

/**
 * Build the instalment schedule for a payment plan: `count` payments from
 * `startDate` at `frequency`, splitting `totalAmount` evenly with the rounding
 * remainder added to the final payment so the instalments sum to the exact total.
 * Each instalment also carries the running balance left after it, so the review
 * screen can show the balance stepping down to zero.
 */
export function buildPlanSchedule(
  totalAmount: number,
  count: number,
  startDate: Date,
  frequency: PlanFrequency,
): PlanInstalment[] {
  const totalSubunits = toSubunits(totalAmount);
  const base = baseInstalmentSubunits(totalAmount, count);
  const remainder = totalSubunits - base * count;
  const instalments: PlanInstalment[] = [];
  let remaining = totalSubunits;
  for (let i = 0; i < count; i++) {
    const subunits = i === count - 1 ? base + remainder : base;
    remaining -= subunits;
    instalments.push({ date: addFrequencyOffset(startDate, frequency, i), subunits, balanceAfterSubunits: remaining });
  }
  return instalments;
}

/** Map UI instalments to the `PaymentPlanInstallment` shape the payment API expects. */
export function toApiSchedule(instalments: PlanInstalment[]): PaymentPlanInstallment[] {
  const time_zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return instalments.map((i) => ({ subunits: i.subunits, date: toScheduleDate(i.date), time_zone }));
}

/**
 * Format a Date as the `YYYY-MM-DD` local-date string used in payment plan
 * installment schedules (matches PaymentPlanInstallment.date).
 */
export function toScheduleDate(date: Date): string {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
}

/**
 * One month ahead of `from`, clamped to the target month's last day so a short
 * month can't roll the ceiling forward (e.g. Jan 31 + 1mo → Feb 28, not Mar 3).
 *
 * The far edge of the window a plan's first payment may fall in, used by the case tab's date
 * picker and by the deep-link clamp in `lib/redirect.ts`. `AccountPaymentPlanTab` still derives
 * its own ceiling with a bare `setMonth`, which rolls forward from a short month — so the two can
 * differ by a few days at a month end. Harmless in that direction only: this clamp is never
 * looser than that picker, so a prefilled date always lands inside it.
 */
export function oneMonthAhead(from: Date): Date {
  const day = from.getDate();
  const r = new Date(from);
  r.setDate(1);
  r.setMonth(r.getMonth() + 1);
  r.setDate(Math.min(day, new Date(r.getFullYear(), r.getMonth() + 1, 0).getDate()));
  return r;
}

/**
 * Build a single-installment schedule for a scheduled (future-dated) full/partial
 * payment. A future charge can only be taken as a plan, so the whole amount rides
 * one installment on the selected date while payment_type stays full/partial.
 */
export function buildScheduleForDate(amount: number, date: Date): PaymentPlanInstallment[] {
  return [
    {
      subunits: toSubunits(amount),
      date: toScheduleDate(date),
      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  ];
}

/**
 * Whether an installment due on `dateStr` (YYYY-MM-DD) falls within `leadDays`
 * of today — the window in which a method's payment intent has already been
 * created upstream (e.g. Bacs, ~7 days before the due date), so cancelling the
 * plan cannot stop that specific charge.
 */
export function isWithinLeadDays(dateStr: string, leadDays: number): boolean {
  const due = new Date(`${dateStr}T00:00:00`);
  const now = new Date();
  // UTC calendar day, matching connect-api's lead-time anchor
  // (datetime.now(UTC).date()) so this warning agrees with the server
  // regardless of the browser's timezone — see methodDateAvailability().
  const today = new Date(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const diffDays = Math.round((due.getTime() - today.getTime()) / 86_400_000);
  return diffDays <= leadDays;
}
